import { http, isObject, unwrap } from './http'

/** Rutas de `docs/API.md` § Autenticación. */
export const authApi = {
  /** @param {{ email: string, password: string }} credentials */
  login(credentials) {
    return unwrap(http.post('/auth/login', credentials), isObject)
  },

  /**
   * Rota el refresh token (cookie httpOnly) y devuelve un token de acceso nuevo.
   * `__skipAuthRefresh` evita que un 401 aquí dispare otro refresco en cadena.
   *
   * `organizationId` es OPCIONAL y sólo una pista: el ámbito que esta pestaña
   * quiere conservar (`null` = consola de plataforma). No concede nada — el
   * backend la honra si el usuario tiene derecho a ese ámbito y, si no, la
   * ignora y resuelve por su cuenta. Se omite del cuerpo cuando la pestaña
   * todavía no tiene ámbito propio, porque "sin preferencia" y "plataforma" no
   * son lo mismo.
   *
   * @param {string|null|undefined} organizationId
   */
  refresh(organizationId) {
    const body = organizationId === undefined ? null : { organizationId }
    return unwrap(http.post('/auth/refresh', body, { __skipAuthRefresh: true }), isObject)
  },

  logout() {
    return http.post('/auth/logout', null, { __skipAuthRefresh: true })
  },

  /**
   * Usuario, organización activa, rol, permisos efectivos, suplantación y
   * `memberships`: las organizaciones utilizables por este usuario.
   */
  me() {
    return unwrap(http.get('/auth/me'), isObject)
  },

  /**
   * Cambia el ámbito de la sesión sin pasar por la suplantación.
   *
   * Es para el caso del operador que además es dueño de una organización real:
   * el backend prefiere siempre el ámbito de plataforma, así que sin esto nunca
   * llegaría al panel de su propia organización. Suplantarse a uno mismo no
   * vale — dejaría un rastro de acceso de soporte donde hay una membresía
   * legítima.
   *
   * Devuelve `{ accessToken, expiresIn, me }` (la misma forma que el login) y
   * rota la cookie de refresco, así que el cambio sobrevive a una recarga.
   *
   * @param {string|null} organizationId `null` vuelve al ámbito de plataforma.
   */
  switchOrganization(organizationId = null) {
    return unwrap(http.post('/auth/switch-organization', { organizationId }), isObject)
  },

  /** @param {string} email */
  forgotPassword(email) {
    return http.post('/auth/password/forgot', { email })
  },

  /** @param {{ token: string, password: string }} payload */
  resetPassword(payload) {
    return http.post('/auth/password/reset', payload)
  },

  /**
   * Cambio de contraseña con la actual.
   *
   * El backend revoca TODOS los refresh tokens del usuario y borra la cookie de
   * refresco: al volver, la sesión ya no se puede renovar. Quien llame a esto
   * tiene que cerrar sesión y mandar al login, no fingir que no ha pasado nada.
   *
   * @param {{ currentPassword: string, newPassword: string }} payload
   */
  changePassword(payload) {
    return http.post('/auth/password/change', payload)
  },
}
